import _, { isArray } from 'lodash'
import produce from 'immer'
import { AnyObject, Collection } from '@feedyou/utils'
import { generateMissingId, regenerateDuplicateId } from './idGeneration'

export type IdAccumulators = { duplicateIds: Set<string>; encounteredIds: Set<string> }

export type FindAndFixDuplicateIdsParams<SearchedEntity> = {
  searchedEntity: SearchedEntity
  entityUniqueIdsSchema?: UniqueIdsSchema
  idAccumulatorsMUTABLE: IdAccumulators
}

/**
 * This code leverages the performance and reference persistence benefits of immer.js,
 * This is why it wraps everything in `produce` and refuses to use Array.map() so stubbornly!
 *
 * This way searched objects are cloned/copied only when they are modified (big performance boost).
 * Each entity and sub-entities also keep their original reference, unless modified!
 *
 * So this should pass when no duplicate ids found:
 * someStructure === findAndFixDuplicateIds(someStructure, accumulators, someSchema)
 */
export const findAndFixDuplicateIds = <SearchedEntity extends object>(
  params: FindAndFixDuplicateIdsParams<SearchedEntity>
) => {
  const { searchedEntity, entityUniqueIdsSchema = {} as NonCollectionUniqueIdSchema } = params

  return produce(searchedEntity, searchedEntityDraft => {
    if (entityUniqueIdsSchema.isCollection) {
      return findAndFixDuplicateIdsInCollection(params) as any
    }

    findAndFixDuplicateIdsInNestedEntities(params).forEach(fixedNestedEntityEntry => {
      const [nestedEntityKey, fixedNestedEntity] = fixedNestedEntityEntry

      if (fixedNestedEntity) {
        _.set(searchedEntityDraft, nestedEntityKey, fixedNestedEntity)
      }
    })

    if (entityUniqueIdsSchema.hasNoId) {
      return
    }

    const fixedEntity = findAndFixDuplicateIdsInEntityWithId(params as any)
    ;(searchedEntityDraft as any).id = fixedEntity.id
  })
}

const findAndFixDuplicateIdsInEntityWithId = ({
  searchedEntity,
  idAccumulatorsMUTABLE: { duplicateIds, encounteredIds },
  entityUniqueIdsSchema
}: FindAndFixDuplicateIdsParams<{ id: string }>) => {
  if (isArray(searchedEntity)) {
    logUnexpectedArrayError(searchedEntity, entityUniqueIdsSchema)
  }

  return produce(searchedEntity, draft => {
    if (!draft.id) {
      const newId = generateMissingId()
      draft.id = newId
      encounteredIds.add(newId)
      return
    }

    if (encounteredIds.has(draft.id)) {
      duplicateIds.add(draft.id!)
      const newId = regenerateDuplicateId()
      draft.id = newId
      encounteredIds.add(newId)
      return
    }

    encounteredIds.add(draft.id)
    return
  })
}

const findAndFixDuplicateIdsInNestedEntities = ({
  searchedEntity,
  entityUniqueIdsSchema = {},
  idAccumulatorsMUTABLE
}: FindAndFixDuplicateIdsParams<any>): [string, any][] => {
  const { nestedEntities = [] } = entityUniqueIdsSchema as NonCollectionUniqueIdSchema

  return nestedEntities.map(nestedEntityDescriptor => {
    const { key } = nestedEntityDescriptor

    const nestedEntity = _.get(searchedEntity, key) as AnyObject | any[] | undefined

    if (!nestedEntity) {
      return [key, null]
    }

    const fixedSubEntity = findAndFixDuplicateIds({
      searchedEntity: nestedEntity,
      entityUniqueIdsSchema: nestedEntityDescriptor,
      idAccumulatorsMUTABLE
    })

    return [key, fixedSubEntity]
  })
}

const findAndFixDuplicateIdsInCollection = (
  params: FindAndFixDuplicateIdsParams<Collection<any>>
) => {
  const { collectionElementsShape } = params.entityUniqueIdsSchema as CollectionUniqueIdsSchema

  return produce(params.searchedEntity, collectionDraft => {
    Object.entries(collectionDraft).forEach(([key, entity]) => {
      // @ts-ignore
      collectionDraft[key] = findAndFixDuplicateIds({
        ...params,
        searchedEntity: entity,
        entityUniqueIdsSchema: collectionElementsShape
      })
    })
  })
}

const logUnexpectedArrayError = (searchedEntity: any, entityShapeDescriptor: any) => {
  console.error(
    'Cannot set id to an array. You most likely forgot to identify a collection in you shape descriptor with { isCollection: true }. Here is the faulty entity: ',
    searchedEntity,
    'and here is its descriptor: ',
    entityShapeDescriptor
  )
}

export type CollectionUniqueIdsSchema = {
  isCollection: true
  collectionElementsShape?: UniqueIdsSchema
}

export type NonCollectionUniqueIdSchema = {
  isCollection?: false
  nestedEntities?: (UniqueIdsSchema & { key: string })[]
  // Indicates that this object contains sub-entites but should have no id.
  // This way we know not it's not missing, and do not generate it!
  hasNoId?: boolean
}

export type UniqueIdsSchema = CollectionUniqueIdsSchema | NonCollectionUniqueIdSchema
